import React from 'react';

import { normalizeCollectionResponse, normalizeRecordResponse } from '@playbooks/api';
import { useStorage } from '@playbooks/contexts';
import {
	AUTH_COOKIE_NAME,
	formatAuthHeaders,
	TEMP_AUTH_COOKIE_NAME,
	TEMP_WORKSPACE_COOKIE_NAME,
} from '@playbooks/utils/auth';

type StoreContextProps = {
	computeHeaders: (v?) => any;
	computeParams: (v?) => any;
	query: any;
	queryRecord: any;
	createRecord: any;
	updateRecord: any;
	saveRecord: any;
	saveRecords: any;
	deleteRecord: any;
	request: any;
	download: any;
};

type StoreProps = {
	method: string;
	url: string;
	headers?: any;
	params?: any;
	data?: any;
	signal?: AbortSignal;
};

const StoreContext = React.createContext<StoreContextProps>(null);

const StoreProvider = ({ client, admin = false, children }) => {
	const storage = useStorage();

	// Computed
	const computeHeaders = (headers?) => {
		const isAdmin =
			admin || (typeof window !== 'undefined' && window.location.href?.includes(process.env.NEXT_PUBLIC_ADMIN_CLIENT));
		return formatAuthHeaders(
			{
				workspace: storage.getCookie('workspace'),
				[AUTH_COOKIE_NAME]: storage.getCookie(AUTH_COOKIE_NAME),
				[TEMP_WORKSPACE_COOKIE_NAME]: storage.getCookie(TEMP_WORKSPACE_COOKIE_NAME),
				[TEMP_AUTH_COOKIE_NAME]: storage.getCookie(TEMP_AUTH_COOKIE_NAME),
			},
			{ admin: isAdmin, headers },
		);
	};

	const computeParams = (params?) => {
		const context = params?.context || [];
		if (admin) context.push(`admin`);
		return context.length > 0 ? { ...params, context } : { ...params };
	};

	// Methods
	const query = async ({ method = 'GET', url, headers, params, signal }: StoreProps) => {
		const formattedHeaders = computeHeaders(headers);
		const formattedParams = computeParams(params);
		const response = await client.storeRequest({
			method,
			url,
			headers: formattedHeaders,
			params: formattedParams,
			signal,
		});
		return normalizeCollectionResponse(response);
	};

	const queryRecord = async ({ method = 'GET', url, headers, params, signal }: StoreProps) => {
		const formattedHeaders = computeHeaders(headers);
		const formattedParams = computeParams(params);
		const response = await client.storeRequest({
			method,
			url,
			headers: formattedHeaders,
			params: formattedParams,
			signal,
		});
		return normalizeRecordResponse(response);
	};

	const saveRecord = async ({ url, headers, params, data }: StoreProps) => {
		return data.id
			? await updateRecord({ method: 'PUT', url, headers, params, data })
			: await createRecord({ method: 'POST', url, headers, params, data });
	};

	const saveRecords = async ({ method = 'PUT', url, headers, params, data }: StoreProps) => {
		const formattedHeaders = computeHeaders(headers);
		const formattedParams = computeParams(params);
		const response = await client.storeRequest({
			method,
			url,
			headers: formattedHeaders,
			params: formattedParams,
			data,
		});
		return normalizeCollectionResponse(response);
	};

	const createRecord = async ({ method = 'POST', url, headers, params, data }: StoreProps) => {
		const formattedHeaders = computeHeaders(headers);
		const formattedParams = computeParams(params);
		const response = await client.storeRequest({
			method,
			url,
			headers: formattedHeaders,
			params: formattedParams,
			data,
		});
		return normalizeRecordResponse(response);
	};

	const updateRecord = async ({ method = 'PUT', url, headers, params, data }: StoreProps) => {
		const formattedHeaders = computeHeaders(headers);
		const formattedParams = computeParams(params);
		const response = await client.storeRequest({
			method,
			url,
			headers: formattedHeaders,
			params: formattedParams,
			data,
		});
		return normalizeRecordResponse(response);
	};

	const deleteRecord = async ({ method = 'DELETE', url, headers, params, data }: StoreProps) => {
		const formattedHeaders = computeHeaders(headers);
		const formattedParams = computeParams(params);
		const response = await client.storeRequest({
			method,
			url,
			headers: formattedHeaders,
			params: formattedParams,
			data,
		});
		return normalizeRecordResponse(response);
	};

	const request = async ({ method = 'GET', url, headers, params, data }: StoreProps) => {
		const formattedHeaders = computeHeaders(headers);
		const formattedParams = computeParams(params);
		return await client.apiRequest({ method, url, headers: formattedHeaders, params: formattedParams, data });
	};

	const download = async ({ method = 'GET', url, headers, params, data }: StoreProps) => {
		const formattedHeaders = computeHeaders(headers);
		const formattedParams = computeParams(params);
		return await client.downloadRequest({ method, url, headers: formattedHeaders, params: formattedParams, data });
	};

	// Render
	return (
		<StoreContext.Provider
			value={{
				computeHeaders,
				computeParams,
				query,
				queryRecord,
				saveRecord,
				saveRecords,
				createRecord,
				updateRecord,
				deleteRecord,
				request,
				download,
			}}>
			{children}
		</StoreContext.Provider>
	);
};

const useStore = () => {
	return React.useContext(StoreContext);
};

export { StoreProvider, useStore };
